import { c as _c } from "react/compiler-runtime";
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { analyticsApi } from '../api/endpoints';
import type { DateRange, EngagementAnalytics, TrendAnalytics, ProductInsightsResponse, InitiativeMetricsResponse, SummaryResponse } from '../api/analytics-types';
import { useEntity } from './useEntities';

// ============================================================================
// Query Keys
// ============================================================================

export const queryKeys = {
  entityAnalytics: (entityId: number) => ['entity-analytics', entityId] as const,
  summary: (entityId: number, startDate?: string, endDate?: string) => [...queryKeys.entityAnalytics(entityId), 'summary', startDate, endDate] as const,
  engagement: (entityId: number, startDate?: string, endDate?: string) => [...queryKeys.entityAnalytics(entityId), 'engagement', startDate, endDate] as const,
  trends: (entityId: number, startDate?: string, endDate?: string, period?: string) => [...queryKeys.entityAnalytics(entityId), 'trends', startDate, endDate, period] as const,
  products: (entityId: number, sortBy?: string, limit?: number, startDate?: string, endDate?: string) => [...queryKeys.entityAnalytics(entityId), 'products', sortBy, limit, startDate, endDate] as const,
  initiatives: (entityId: number, status?: string, startDate?: string, endDate?: string) => [...queryKeys.entityAnalytics(entityId), 'initiatives', status, startDate, endDate] as const
} as const;

// ============================================================================
// API Client Functions
// ============================================================================

/**
 * Fetch brand analytics summary
 */
async function fetchEntitySummary(entityId: number, startDate?: string, endDate?: string, ignoreCache?: string): Promise<SummaryResponse> {
  return analyticsApi.getEntitySummary(entityId, {
    startDate,
    endDate,
    ignoreCache
  });
}

/**
 * Fetch engagement analytics
 */
async function fetchEngagementAnalytics(params: {
  entityId: number;
  startDate?: string;
  endDate?: string;
  ignoreCache?: string;
}): Promise<EngagementAnalytics> {
  return analyticsApi.getBrandEngagement(params.entityId, {
    startDate: params.startDate,
    endDate: params.endDate,
    ignoreCache: params.ignoreCache
  });
}

/**
 * Fetch trend analytics
 */
async function fetchTrendAnalytics(params: {
  entityId: number;
  startDate?: string;
  endDate?: string;
  period?: 'daily' | 'weekly' | 'monthly';
  ignoreCache?: string;
}): Promise<TrendAnalytics> {
  return analyticsApi.getBrandTrends(params.entityId, {
    startDate: params.startDate,
    endDate: params.endDate,
    period: params.period,
    ignoreCache: params.ignoreCache
  });
}

/**
 * Fetch product insights
 */
async function fetchProductInsights(params: {
  entityId: number;
  sortBy?: 'reactions' | 'collections' | 'engagement';
  limit?: number;
  startDate?: string;
  endDate?: string;
  ignoreCache?: string;
}): Promise<ProductInsightsResponse> {
  return analyticsApi.getBrandProducts(params.entityId, {
    sortBy: params.sortBy,
    limit: params.limit,
    startDate: params.startDate,
    endDate: params.endDate,
    ignoreCache: params.ignoreCache
  });
}

/**
 * Fetch initiative metrics
 */
async function fetchInitiativeMetrics(params: {
  entityId: number;
  status?: string;
  startDate?: string;
  endDate?: string;
  ignoreCache?: string;
}): Promise<InitiativeMetricsResponse> {
  return analyticsApi.getBrandInitiatives(params.entityId, {
    status: params.status,
    startDate: params.startDate,
    endDate: params.endDate,
    ignoreCache: params.ignoreCache
  });
}

// ============================================================================
// Hook Parameters
// ============================================================================

export interface UseEntityAnalyticsParams {
  entityId: number;
  startDate?: string;
  endDate?: string;
  period?: 'daily' | 'weekly' | 'monthly';
  sortBy?: 'reactions' | 'collections' | 'engagement';
  limit?: number;
  status?: string;
  ignoreCache?: string;
}

// ============================================================================
// Main Hook
// ============================================================================

/**
 * Hook to fetch all brand analytics data with parallel queries
 *
 * Features:
 * - Parallel data loading for all analytics sections
 * - 5-minute staleTime for all queries
 * - Comprehensive loading and error states
 * - Manual refetch function for all queries
 *
 * @param params - Analytics query parameters
 * @returns Analytics data, loading states, error states, and refetch function
 */
export function useEntityAnalytics(params) {
  const $ = _c(75);
  let t0;
  if ($[0] !== params.entityId) {
    t0 = params.entityId.toString();
    $[0] = params.entityId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const entity = useEntity(t0);
  let t1;
  let t2;
  if ($[2] !== params.endDate || $[3] !== params.entityId || $[4] !== params.ignoreCache || $[5] !== params.startDate) {
    t1 = [...queryKeys.summary(params.entityId, params.startDate, params.endDate), params.ignoreCache];
    t2 = () => fetchEntitySummary(params.entityId, params.startDate, params.endDate, params.ignoreCache);
    $[2] = params.endDate;
    $[3] = params.entityId;
    $[4] = params.ignoreCache;
    $[5] = params.startDate;
    $[6] = t1;
    $[7] = t2;
  } else {
    t1 = $[6];
    t2 = $[7];
  }
  const t3 = !!params.entityId;
  let t4;
  if ($[8] !== t1 || $[9] !== t2 || $[10] !== t3) {
    t4 = {
      queryKey: t1,
      queryFn: t2,
      staleTime: 10000,
      enabled: t3
    };
    $[8] = t1;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const summaryQuery = useQuery(t4);
  let t5;
  let t6;
  if ($[12] !== params.endDate || $[13] !== params.entityId || $[14] !== params.ignoreCache || $[15] !== params.startDate) {
    t5 = [...queryKeys.engagement(params.entityId, params.startDate, params.endDate), params.ignoreCache];
    t6 = () => fetchEngagementAnalytics({
      entityId: params.entityId,
      startDate: params.startDate,
      endDate: params.endDate,
      ignoreCache: params.ignoreCache
    });
    $[12] = params.endDate;
    $[13] = params.entityId;
    $[14] = params.ignoreCache;
    $[15] = params.startDate;
    $[16] = t5;
    $[17] = t6;
  } else {
    t5 = $[16];
    t6 = $[17];
  }
  const t7 = !!params.entityId;
  let t8;
  if ($[18] !== t5 || $[19] !== t6 || $[20] !== t7) {
    t8 = {
      queryKey: t5,
      queryFn: t6,
      staleTime: 10000,
      enabled: t7
    };
    $[18] = t5;
    $[19] = t6;
    $[20] = t7;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  const engagementQuery = useQuery(t8);
  let t10;
  let t9;
  if ($[22] !== params.endDate || $[23] !== params.entityId || $[24] !== params.ignoreCache || $[25] !== params.period || $[26] !== params.startDate) {
    t9 = [...queryKeys.trends(params.entityId, params.startDate, params.endDate, params.period), params.ignoreCache];
    t10 = () => fetchTrendAnalytics({
      entityId: params.entityId,
      startDate: params.startDate,
      endDate: params.endDate,
      period: params.period,
      ignoreCache: params.ignoreCache
    });
    $[22] = params.endDate;
    $[23] = params.entityId;
    $[24] = params.ignoreCache;
    $[25] = params.period;
    $[26] = params.startDate;
    $[27] = t10;
    $[28] = t9;
  } else {
    t10 = $[27];
    t9 = $[28];
  }
  const t11 = !!params.entityId;
  let t12;
  if ($[29] !== t10 || $[30] !== t11 || $[31] !== t9) {
    t12 = {
      queryKey: t9,
      queryFn: t10,
      staleTime: 10000,
      enabled: t11
    };
    $[29] = t10;
    $[30] = t11;
    $[31] = t9;
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  const trendsQuery = useQuery(t12);
  let t13;
  let t14;
  if ($[33] !== params.endDate || $[34] !== params.entityId || $[35] !== params.ignoreCache || $[36] !== params.limit || $[37] !== params.sortBy || $[38] !== params.startDate) {
    t13 = [...queryKeys.products(params.entityId, params.sortBy, params.limit, params.startDate, params.endDate), params.ignoreCache];
    t14 = () => fetchProductInsights({
      entityId: params.entityId,
      sortBy: params.sortBy,
      limit: params.limit,
      startDate: params.startDate,
      endDate: params.endDate,
      ignoreCache: params.ignoreCache
    });
    $[33] = params.endDate;
    $[34] = params.entityId;
    $[35] = params.ignoreCache;
    $[36] = params.limit;
    $[37] = params.sortBy;
    $[38] = params.startDate;
    $[39] = t13;
    $[40] = t14;
  } else {
    t13 = $[39];
    t14 = $[40];
  }
  const t15 = !!params.entityId;
  let t16;
  if ($[41] !== t13 || $[42] !== t14 || $[43] !== t15) {
    t16 = {
      queryKey: t13,
      queryFn: t14,
      staleTime: 10000,
      enabled: t15
    };
    $[41] = t13;
    $[42] = t14;
    $[43] = t15;
    $[44] = t16;
  } else {
    t16 = $[44];
  }
  const productsQuery = useQuery(t16);
  let t17;
  let t18;
  if ($[45] !== params.endDate || $[46] !== params.entityId || $[47] !== params.ignoreCache || $[48] !== params.startDate || $[49] !== params.status) {
    t17 = [...queryKeys.initiatives(params.entityId, params.status, params.startDate, params.endDate), params.ignoreCache];
    t18 = () => fetchInitiativeMetrics({
      entityId: params.entityId,
      status: params.status,
      startDate: params.startDate,
      endDate: params.endDate,
      ignoreCache: params.ignoreCache
    });
    $[45] = params.endDate;
    $[46] = params.entityId;
    $[47] = params.ignoreCache;
    $[48] = params.startDate;
    $[49] = params.status;
    $[50] = t17;
    $[51] = t18;
  } else {
    t17 = $[50];
    t18 = $[51];
  }
  const t19 = !!params.entityId;
  let t20;
  if ($[52] !== t17 || $[53] !== t18 || $[54] !== t19) {
    t20 = {
      queryKey: t17,
      queryFn: t18,
      staleTime: 10000,
      enabled: t19
    };
    $[52] = t17;
    $[53] = t18;
    $[54] = t19;
    $[55] = t20;
  } else {
    t20 = $[55];
  }
  const initiativesQuery = useQuery(t20);
  const refetchAll = _temp;
  const t21 = summaryQuery.isLoading || engagementQuery.isLoading || trendsQuery.isLoading || productsQuery.isLoading || initiativesQuery.isLoading;
  const t22 = summaryQuery.isError || engagementQuery.isError || trendsQuery.isError || productsQuery.isError || initiativesQuery.isError;
  let t23;
  if ($[56] !== engagementQuery.data || $[57] !== engagementQuery.error || $[58] !== engagementQuery.isLoading || $[59] !== entity.data || $[60] !== initiativesQuery.data || $[61] !== initiativesQuery.error || $[62] !== initiativesQuery.isLoading || $[63] !== productsQuery.data || $[64] !== productsQuery.error || $[65] !== productsQuery.isLoading || $[66] !== summaryQuery.data || $[67] !== summaryQuery.error || $[68] !== summaryQuery.isLoading || $[69] !== t21 || $[70] !== t22 || $[71] !== trendsQuery.data || $[72] !== trendsQuery.error || $[73] !== trendsQuery.isLoading) {
    t23 = {
      brand: entity.data,
      summary: summaryQuery.data,
      engagement: engagementQuery.data,
      trends: trendsQuery.data,
      products: productsQuery.data,
      initiatives: initiativesQuery.data,
      isLoading: t21,
      isSummaryLoading: summaryQuery.isLoading,
      isEngagementLoading: engagementQuery.isLoading,
      isTrendsLoading: trendsQuery.isLoading,
      isProductsLoading: productsQuery.isLoading,
      isInitiativesLoading: initiativesQuery.isLoading,
      isError: t22,
      summaryError: summaryQuery.error,
      engagementError: engagementQuery.error,
      trendsError: trendsQuery.error,
      productsError: productsQuery.error,
      initiativesError: initiativesQuery.error,
      refetchAll
    };
    $[56] = engagementQuery.data;
    $[57] = engagementQuery.error;
    $[58] = engagementQuery.isLoading;
    $[59] = entity.data;
    $[60] = initiativesQuery.data;
    $[61] = initiativesQuery.error;
    $[62] = initiativesQuery.isLoading;
    $[63] = productsQuery.data;
    $[64] = productsQuery.error;
    $[65] = productsQuery.isLoading;
    $[66] = summaryQuery.data;
    $[67] = summaryQuery.error;
    $[68] = summaryQuery.isLoading;
    $[69] = t21;
    $[70] = t22;
    $[71] = trendsQuery.data;
    $[72] = trendsQuery.error;
    $[73] = trendsQuery.isLoading;
    $[74] = t23;
  } else {
    t23 = $[74];
  }
  return t23;
}
function _temp() {
  return Date.now().toString();
}